import { useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { useGetProductInfo } from "common/hooks/user/Products/useGetProductInfo";
import { enum_api_uri } from "common/hooks/utils/enum";
import { makeIntComma } from "common/hooks/utils/utils";
import { OutletContextProps } from "./-components/formik-utils";
import RefreshRedirect from "./-components/RefreshRedirect";
import useOverlay, { OverlayWrapper } from "common/hooks/user/useOverlay";
import { cancelMessage, SystemPopup, ViewLargeImg } from "user/components/common/Overlay";
import Header from "user/components/common/Header";
import Progressbar from "./-components/Progressbar";
import Spinner from "common/components/spinner";
import { ReactComponent as ZoomIcon } from "../../assets/icons/zoom_glasses.svg";
import { ReactComponent as ShppingBag } from "../../assets/icons/shoppingbag.svg";
import "swiper/css";

export default function ProductDetail({ update = false }: { update?: boolean }) {
    const [slideActiveIndex, setSlideActiveIndex] = useState(0);
    const product_idx = useParams()?.idx;
    const context = useOutletContext<OutletContextProps>();
    const formik = context.formik;
    const { handleSubmit, values, setValues } = formik.product;
    const { data, isPending } = useGetProductInfo(Number(product_idx));
    const { overlay, closeOverlay, openOverlay } = useOverlay();
    const [orientations, setOrientations] = useState<Record<string, boolean>>({});

    const handleImageLoad = (fileIdx: number, e: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        const isHorizontal = naturalWidth >= naturalHeight;
        setOrientations(prev => ({
            ...prev,
            [fileIdx]: isHorizontal,
        }));
    };

    return (
        <RefreshRedirect customerIdx={formik.from.values.customer_idx} isUpdate={update}>
            <OverlayWrapper overlay={overlay} closeOverlay={closeOverlay}>
                <div className="flex-1 flex flex-col min-h-[100dvh] max-h-[100dvh] bg-white">
                    {isPending && <Spinner />}
                    <div className="sticky top-0 bg-white z-[2]">
                        <Header
                            title="2. 상품 선택"
                            goBack
                            onClickClose={() => openOverlay(<SystemPopup isConfirm message={cancelMessage(update)} onClickClose={closeOverlay} />)}
                        />
                        <Progressbar step={2} className="wrapper" />
                    </div>

                    <div className="flex-1 overflow-auto">
                        <div className="relative after:content-[''] after:block after:pb-[100%] overflow-hidden">
                            <div className="absolute inset-0">
                                {data?.product_image && data?.product_image.length > 0 ? (
                                    <div className="relative h-full">
                                        <Swiper
                                            slidesPerView={1}
                                            pagination
                                            modules={[Pagination]}
                                            onSlideChange={(slide: any) => setSlideActiveIndex(slide.activeIndex)}
                                            className="h-full tablet:[&_.swiper-pagination]:!top-[100dvw] tablet:[&_.swiper-pagination]:!-translate-y-[25px] [&_.swiper-pagination]:left-1/2 [&_.swiper-pagination]:-translate-x-1/2 [&_.swiper-pagination]:absolute [&_.swiper-pagination]:z-[111] [&_.swiper-pagination]:top-[743px] [&_.swiper-pagination]:flex [&_.swiper-pagination]:justify-center [&_.swiper-pagination]:items-center [&_.swiper-pagination]:gap-[8px] [&_.swiper-pagination-bullet]:w-[8px] [&_.swiper-pagination-bullet]:h-[8px] [&_.swiper-pagination-bullet]:rounded-full [&_.swiper-pagination-bullet]:transition-all [&_.swiper-pagination-bullet]:duration-[0.3s] [&_.swiper-pagination-bullet]:bg-[#DADEE4] [&_.swiper-pagination-bullet-active]:!w-[40px] [&_.swiper-pagination-bullet-active]:!bg-[#484964] [&_.swiper-pagination-bullet-active]:rounded-[20px]"
                                        >
                                            {data?.product_image.map(item => {
                                                const isHorizontal = orientations[item.file_idx];

                                                return (
                                                    <SwiperSlide
                                                        key={item.file_idx}
                                                        className="flex justify-center items-center px-12 py-12"
                                                    >
                                                        <img
                                                            src={enum_api_uri.api_uri + "/" + item.file_name}
                                                            alt={item.file_name}
                                                            onLoad={e => handleImageLoad(item.file_idx, e)}
                                                            className={`${isHorizontal ? "w-full" : "h-full"} object-cover`}
                                                        />
                                                    </SwiperSlide>
                                                );
                                            })}
                                        </Swiper>
                                    </div>
                                ) : (
                                    <div className="flex justify-center items-center">이미지 없음</div>
                                )}
                            </div>
                            {data?.product_image && data?.product_image.length > 0 && (
                                <button
                                    type="button"
                                    title="이미지 크게 보기"
                                    className="absolute z-[1] right-[23px] bottom-[23px]"
                                    onClick={() => {
                                        if (data?.notice_image) {
                                            openOverlay(
                                                <ViewLargeImg
                                                    src={enum_api_uri.api_uri + "/" + data.product_image[slideActiveIndex].file_name}
                                                    closeOverlay={closeOverlay}
                                                />,
                                            );
                                        }
                                    }}
                                >
                                    <ZoomIcon />
                                </button>
                            )}
                        </div>

                        <div>
                            <div className="wrapper border-t border-[#E5E5F1] py-[20px] flex items-end justify-between gap-[20px]">
                                <div className="flex-1">
                                    <span className="text-[14px] leading-[20px] text-[#999999] font-[500] mb-[40px] break-all">
                                        {data?.product_category.category_name}
                                    </span>
                                    <p className="text-[18px] leading-[28px] font-bold">{data?.product_name}</p>
                                </div>
                                <p className="text-[24px] leading-[36px] font-bold text-nowrap">
                                    {data && makeIntComma(data.product_amount)}원
                                </p>
                            </div>
                            {data?.notice_image.map((img, index) => {
                                return (
                                    <figure key={index}>
                                        <img src={`${enum_api_uri.api_uri}/${img.file_name}`} alt={img.file_name} className="w-full" />
                                    </figure>
                                );
                            })}
                            <div className="wrapper py-[20px] text-center">{data?.product_content}</div>
                        </div>
                    </div>

                    <div className="wrapper bg-white border-t border-[#dddddd] py-[12px]">
                        <button
                            type="button"
                            className="justify-center py-[12px] text-[18px] leading-[28px] bg-[#273219] rounded-[12px] text-white font-bold w-full flex items-center gap-[8px]"
                            // 다음 단계로 이동, 오버레이 닫기
                            onClick={() => {
                                if (data) {
                                    setValues({
                                        ...values,
                                        product_idx: data.product_idx,
                                        union_option: data.product_category.union_option,
                                    });
                                    // setToValues({
                                    //     ...toValues,
                                    //     is_union: data.product_category.union_option,
                                    //     wreath_sender: "",
                                    //     wreath_word: "",
                                    // });
                                    // setToFieldValue("is_union", data.product_category.union_option);
                                }
                                handleSubmit();
                            }}
                        >
                            <ShppingBag />
                            상품 선택
                        </button>
                    </div>
                </div>
            </OverlayWrapper>
        </RefreshRedirect>
    );
}
